import { ChevronLeft, ChevronRight, Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/cn';
import { isSameLocalDay as isSameDay } from '@/lib/dateKey';
import { DAY_LONG, DAY_SHORT, MONTH_LONG } from '@/lib/dateNames';
import { Eyebrow, IconButton, KbdHint } from '@/components/ui';
import { useUIStore } from '@/stores/uiStore';

const ZOOM_STEP = 5;
const STRIP_DAYS = 7;

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function shiftDays(d: Date, delta: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + delta);
  return x;
}

export function DayStripTopbar() {
  const date = useUIStore((s) => s.lastViewedDate);
  const setDate = useUIStore((s) => s.setLastViewedDate);
  const hourHeight = useUIStore((s) => s.hourHeightPx);
  const setHourHeight = useUIStore((s) => s.setHourHeight);

  const longDay = DAY_LONG[date.getDay()];
  const dateLabel = `${MONTH_LONG[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;

  // Center the strip on `date`: 3 days before, the day itself, 3 days after.
  const stripStart = shiftDays(date, -Math.floor(STRIP_DAYS / 2));
  const days = Array.from({ length: STRIP_DAYS }, (_, i) => shiftDays(stripStart, i));
  const today = startOfDay(new Date());

  return (
    <div className="border-b border-line bg-canvas">
      <div className="flex items-center justify-between gap-4 px-5 py-3">
        <div>
          <Eyebrow>
            {/* Eyebrow placeholder — populated in Phase 5 with task/event counts */}
            Today's plan
          </Eyebrow>
          {/* v4: display face + lit period (see MobileHeader for the rule) */}
          <h1 className="mt-0.5 font-display text-head font-medium tracking-[-0.01em] text-t1">
            {longDay}
            <span className="text-brand">.</span>
            <span className="ml-2 font-sans text-body font-normal tracking-normal text-t3">
              {dateLabel}
            </span>
          </h1>
        </div>

        <div className="flex items-center gap-1">
          <IconButton
            label="Previous day"
            icon={<ChevronLeft className="h-4 w-4" />}
            onClick={() => setDate(shiftDays(date, -1))}
          />
          <button
            type="button"
            aria-label="Today"
            onClick={() => setDate(new Date())}
            className="flex items-center gap-1.5 rounded-md border border-line-2 bg-bg px-2.5 py-1 text-body text-t1 can-hover:hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            Today
            <KbdHint>T</KbdHint>
          </button>
          <IconButton
            label="Next day"
            icon={<ChevronRight className="h-4 w-4" />}
            onClick={() => setDate(shiftDays(date, 1))}
          />
          <div className="mx-2 h-5 w-px bg-line-2" />
          <IconButton
            label="Zoom out"
            icon={<Minus className="h-4 w-4" />}
            onClick={() => setHourHeight(hourHeight - ZOOM_STEP)}
          />
          <IconButton
            label="Zoom in"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setHourHeight(hourHeight + ZOOM_STEP)}
          />
        </div>
      </div>

      <div className="flex items-center gap-1 px-5 pb-3">
        {days.map((d) => {
          const selected = isSameDay(d, date);
          const isToday = isSameDay(d, today);
          const weekend = d.getDay() === 0 || d.getDay() === 6;
          return (
            <button
              key={d.toISOString()}
              type="button"
              // Full weekday + month so the label stays unambiguous when the
              // 7-day strip spans a month boundary.
              aria-label={d.toLocaleDateString(undefined, {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
              aria-pressed={selected}
              onClick={() => setDate(d)}
              className={cn(
                'flex min-w-[44px] flex-col items-center rounded-md border px-2 py-1.5 pointer-coarse:min-h-[44px]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
                selected
                  ? 'border-t1 bg-card text-t1'
                  : 'border-transparent text-t2 can-hover:hover:bg-card',
                weekend && !selected && 'text-t3',
              )}
            >
              <span className="font-mono text-eyebrow uppercase">{DAY_SHORT[d.getDay()]}</span>
              <span className="font-mono text-body-lg font-medium tabular-nums">{d.getDate()}</span>
              {isToday && !selected && (
                <span className="mt-0.5 h-1 w-1 rounded-full bg-brand" aria-hidden />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
